import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { formatEnvelopeDesignName } from '@apps/card/routes/CardCustomizer/steps/Envelope/envelopeTemplateUtils';
import { isEnvelopeOnlyBlob } from './isEnvelopeOnlyBlob';

export const ENVELOPE_PRODUCT_TYPE = 'envelope';

type EnvelopeOrderCandidate = Readonly<{
  product?: Readonly<{ productType?: string | null }> | null;
  cardDraftJSONBlob?: unknown;
}>;

/**
 * Whether a placed order is a standalone envelope, for the downstream screens that render one.
 *
 * Prefers the order's PRODUCT: the carrier template's category is bookkeeping that exists only to
 * satisfy a non-null FK, so it must not be the signal.
 * Falls back to the blob, because several order queries select `cardDraftJSONBlob` but not
 * `product`.
 */
export const isEnvelopeOrder = (order: EnvelopeOrderCandidate | null | undefined): boolean => {
  if (!order) return false;
  if (order.product?.productType) return order.product.productType === ENVELOPE_PRODUCT_TYPE;

  return isEnvelopeOnlyBlob(order.cardDraftJSONBlob);
};

/**
 * The design name under an envelope order's heading — "Traditional" — or `undefined` for anything
 * else, so callers keep their card branch.
 *
 * From the order's own blob, never its template: the carrier is shared by every envelope order,
 * so naming from it would label them all identically. Legacy drafts store the bare
 * `CardEnvelopeDesign` and new ones the full themeId; `formatEnvelopeDesignName` handles both.
 */
export const getEnvelopeOrderDesignName = (order: EnvelopeOrderCandidate | null | undefined): string | undefined => {
  if (!isEnvelopeOrder(order)) return undefined;

  const design = (order?.cardDraftJSONBlob as DraftJson | null | undefined)?.card?.envelope?.design;
  return design ? formatEnvelopeDesignName(design) : undefined;
};
